import { installDamCallouts } from "./dam-callouts";
import { isLight, lightColor, onThemeIcons, putImage, setLightPaint } from "./theme";
import type { Map as MlMap } from "maplibre-gl";
import { analyze, isSimReady } from "./sim-client";
import { loadRiverNames, riverName, mainRiver } from "./river-names";
import { setDamFlow, clearDamFlow } from "./route-layer";
import { openMapPopup } from "./popup-room";
import { loadDam2011, compare2011Html, c13Max2011Html, setY54Labels, type DamToday } from "./dam-2011";
import type { ReleaseChange, PercentChange } from "@flowsight/sim";
import { SRC_FULL, damReleaseChange, damPercentChange, c13Change, c13Streak, releaseDeltaText, percentDeltaText, thaiDay, fmtCms, type DamPrev, type C13Prev, type C13Recent } from "./dam-change-ui";

/** Dams drawn as a self-explanatory "tank gauge" (water fill ∝ % capacity, colour
 *  by threshold) via a MapLibre symbol layer (canvas images, not DOM markers).
 *  No data → dashed grey tank + "?". The Chao Phraya barrage (no % capacity) →
 *  a discharge arrow + "ระบาย X ลบ.ม./วิ". (v0.8.4) */
interface Dam {
  name: string; lat: number; lon: number; operator: string; egatUrl: string | null;
  kind?: string; // "reservoir" (default) | "diversion" (เขื่อนทดน้ำ, no % capacity)
  capacityMcm: number | null; ridCapacityMcm?: number | null; storageMcm?: number | null; volumeMcm: number | null;
  percent: number | null; percentConsistent?: boolean; impliedPct?: number | null; swocPercent?: number | null;
  basisNote?: string | null; noDataReason?: string | null; // "diversion" | "not-reported" | "inconsistent"
  inflowMcmDay: number | null; inflowCms: number | null;
  outflowMcmDay: number | null; outflowCms: number | null;
  trend: string | null; verified: boolean; staleValue?: boolean; source: string; updatedAt: string; valueDate?: string;
  barrageCms?: number | null; // merged Chao Phraya (C.13) discharge
  barrageDate?: string | null; // its SWOC report date
  baseMismatch?: { appBaseMcm: number; impliedPct: number; swocPct: number } | null; // v0.9.8a base guard
  prev?: DamPrev | null; // previous report (scripts/dam_history.mjs, v0.9.4a)
  relChange?: ReleaseChange | null; pctChange?: PercentChange | null; // derived client-side
  streakText?: string | null; // C.13 "ระบายระดับนี้ต่อเนื่อง N วัน"
}
const THAI_MON = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];
/** "2026-10-01" → "1 ต.ค. 2026" (data date, as shown on the card). */
function thaiDate(iso?: string): string {
  if (!iso) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  return `${Number(m[3])} ${THAI_MON[Number(m[2]) - 1] ?? ""} ${m[1]}`;
}
/** A dam's value is "aged" if carried-forward or its data date is >36h old. */
function isAged(d: Dam): boolean { return !!d.staleValue || isStale(d.valueDate ?? d.updatedAt); }
const SRC = "dams-src";
const LAYER = "dams-layer";    // icon + % value (always visible, zoom 4–18)
const ARROW = "dams-arrow";    // ▲/▼ release change vs the previous report (v0.9.4a A3ก)
let loaded = false;
let dams: Dam[] = [];

function showPct(d: Dam): boolean { return d.percent != null; } // official source % is always shown
function isDiversion(d: Dam): boolean { return d.kind === "diversion"; }
// Diversion dams (and the old Chao Phraya barrage entry) render as a discharge
// barrage, never a reservoir gauge.
function isBarrage(d: Dam): boolean { return isDiversion(d) || (d.capacityMcm == null && d.barrageCms != null); }
function pctColor(p: number | null): string {
  if (p == null) return "#8aa0bf";
  if (p < 60) return "#22e39b";
  if (p < 80) return "#ffb020";
  if (p <= 100) return "#ff8a3c";
  return "#ff3b5c";
}
function isStale(dateStr: string): boolean {
  const t = Date.parse(dateStr + "T00:00:00+07:00");
  return Number.isFinite(t) && Date.now() - t > 36 * 3600 * 1000;
}
const fmt = (n: number | null, d = 0): string => (n == null ? "—" : n.toLocaleString("en-US", { maximumFractionDigits: d }));

/** icon-image key for a dam's state. */
function bucketKey(d: Dam): string {
  if (isBarrage(d)) return "dam-barrage";
  if (!showPct(d)) return "dam-nodata";
  if (d.percent! > 100) return "dam-over";
  return "dam-" + Math.max(0, Math.min(100, Math.round(d.percent! / 10) * 10));
}
/** v0.9.10a: the short name shown on the map above the value — no "เขื่อน", the owner's short forms */
const SHORT: Record<string, string> = { "แควน้อยบำรุงแดน": "แควน้อย", "ป่าสักชลสิทธิ์": "ป่าสัก", "ขุนด่านปราการชล": "ขุนด่าน", "เจ้าพระยา": "เจ้าพระยา C.13" };
function shortName(d: Dam): string { const n = d.name.replace(/^เขื่อน/, "").trim(); return SHORT[n] ?? n; }
/** compact always-on value under the icon (% / discharge / "ทดน้ำ" / "?"). */
function pctText(d: Dam): string {
  if (isBarrage(d)) return d.barrageCms != null ? `${fmt(d.barrageCms)} ลบ.ม./วิ` : "ทดน้ำ";
  if (showPct(d)) return `${Math.round(d.percent!)}%${d.percent! > 100 ? "!" : ""}`; // one whole-number % everywhere (map, card, 2554 table, summary)
  return "?";
}

async function ensure(map: MlMap): Promise<void> {
  if (loaded) return;
  const res = await fetch("/data/dams-status.json");
  if (!res.ok) throw new Error(`dams-status ${res.status}`);
  const data = (await res.json()) as { source?: string; dams?: Dam[]; chaophraya?: { lat: number; lon: number; outflow_cms: number; valueDate?: string | null; prev?: C13Prev | null; recent?: C13Recent[] | null } | null };
  dams = data.dams ?? [];
  void loadDam2011(); // v0.9.7: same day in 2554 for the card (loaded before anyone taps a dam)
  // merge the C.13 discharge into the matching เจ้าพระยา barrage entry
  const c = data.chaophraya;
  if (c) for (const d of dams) if (d.capacityMcm == null && Math.abs(d.lat - c.lat) < 0.05 && Math.abs(d.lon - c.lon) < 0.05) {
    d.barrageCms = c.outflow_cms; d.barrageDate = c.valueDate ?? null; d.relChange = c13Change(c);
    const st = d.relChange?.dir === "same" ? c13Streak(c) : null;
    d.streakText = st ? `ระบายระดับนี้ต่อเนื่อง ${st.days} วัน (ตั้งแต่ ${thaiDay(st.since)})` : null;
  }
  // change vs the previous report — real reports only, none → no arrow (A5)
  for (const d of dams) if (!isBarrage(d) && !d.staleValue) { d.relChange = damReleaseChange(d); d.pctChange = damPercentChange(d); }

  addDamImages(map);
  onThemeIcons(() => addDamImages(map)); // light / dark variant, same shapes (v0.9.9 B5)
  map.addSource(SRC, {
    type: "geojson",
    data: { type: "FeatureCollection", features: dams.map((d, i) => ({
      type: "Feature", id: i,
      geometry: { type: "Point", coordinates: [d.lon, d.lat] },
      properties: { bucket: bucketKey(d), name: shortName(d), pct: pctText(d), stale: isAged(d),
        arr: d.relChange?.arrow ? (d.relChange.dir === "up" ? "dam-arr-up" : "dam-arr-down") : "" },
    })) },
  });
  // icon + % value — ALWAYS visible (no min/max zoom, overlap allowed) so the %
  // never disappears, even on top of a river line. (v0.8.4 item 6)
  // ignore-placement false (v0.9.5c): still always drawn, but river-name labels (lower layer)
  // now see its box and hide instead of printing over the %.
  // v0.9.9a item 2: light mode — near-black % and names on a 2-px white halo (not a hue-kept grey-blue)
  setLightPaint(LAYER, { "text-color": "#111827", "text-halo-color": "#ffffff", "text-halo-width": 2.2 });
  // v0.9.10a: ONE label under the icon — name (always, every zoom) → % / discharge; "ปี 54" sits below it
  // (dam-2011). allow-overlap: never hidden by a collision; ignore-placement false: river names and the
  // stations' "+X ม." (placed after it — they are lower layers) see its box and step aside.
  map.addLayer({
    id: LAYER, type: "symbol", source: SRC, minzoom: 4, layout: {
      "icon-image": ["get", "bucket"], "icon-size": ["interpolate", ["linear"], ["zoom"], 5, 0.62, 10, 0.85], "icon-anchor": "bottom", "icon-allow-overlap": true, "icon-ignore-placement": false,
      "text-field": ["format",
        ["concat", ["get", "name"], ["case", ["get", "stale"], " (เก่า)", ""]], { "font-scale": 0.85 }, "\n", {},
        ["get", "pct"], {}],
      "text-font": ["Noto Sans Regular"], "text-line-height": 1.15,
      "text-size": 13, "text-anchor": "top", "text-offset": [0, 0.25], "text-allow-overlap": true, "text-ignore-placement": false, "text-optional": false,
    }, paint: {
      "icon-opacity": ["case", ["get", "stale"], 0.5, 1],
      "text-color": "#ffffff", "text-halo-color": "#05080f", "text-halo-width": 2, "text-opacity": ["case", ["get", "stale"], 0.7, 1],
    },
  });
  // ▲/▼ beside the icon when the release changed ≥10% (C.13: ≥100 ลบ.ม./วิ) vs the previous report
  map.addLayer({
    id: ARROW, type: "symbol", source: SRC, minzoom: 4, filter: ["!=", ["get", "arr"], ""], layout: {
      "icon-image": ["get", "arr"], "icon-size": ["interpolate", ["linear"], ["zoom"], 5, 0.62, 10, 0.85],
      "icon-anchor": "bottom-left", "icon-offset": [9, -20], "icon-allow-overlap": true, "icon-ignore-placement": false,
    },
  });
  installDamCallouts(map); // v0.9.10a: leader-line callouts when the dam labels would overlap each other
  void loadRiverNames();
  map.on("click", LAYER, (e) => {
    const f = e.features?.[0]; if (f?.id == null) return;
    const d = dams[Number(f.id)]; if (!d) return;
    // trace the downstream flow (glow + names) and tell the card where water goes
    let flowHtml = "";
    if (isSimReady()) {
      const r = analyze(d.lon, d.lat, 2.5);
      if (r) {
        const dischargeZero = !isBarrage(d) && d.outflowCms === 0;
        setDamFlow(map, r.path, dischargeZero);
        const names = flowNames(r.path) + (r.reachesGulf ? " → อ่าวไทย" : "");
        const disc = isBarrage(d) ? `ระบาย ${fmt(d.barrageCms ?? null)} ลบ.ม./วิ`
          : d.outflowCms != null ? `ระบาย ${fmt(d.outflowCms)} ลบ.ม./วิ${d.outflowCms === 0 ? " (SWOC)" : ""}` : "";
        flowHtml = `<div class="dam-flow-line">💧 ${disc ? disc + " · " : ""}น้ำไหลไป: ${esc(names)}</div>`;
      } else {
        flowHtml = `<div class="muted small">⚠️ ไอคอนเขื่อนไม่ติดลำน้ำหลักในระบบ (ลอย)</div>`;
      }
    }
    const popup = openMapPopup(map, [d.lon, d.lat], damCard(d, flowHtml), "280px");
    popup.on("close", () => clearDamFlow(map));
  });
  map.on("mouseenter", LAYER, () => { map.getCanvas().style.cursor = "pointer"; });
  map.on("mouseleave", LAYER, () => { map.getCanvas().style.cursor = ""; });
  loaded = true;
}

export async function setDamsVisible(map: MlMap, on: boolean): Promise<void> {
  if (on) await ensure(map);
  for (const id of [LAYER, ARROW]) if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", on ? "visible" : "none");
}

/* ── tank-gauge canvas images ─────────────────────────────────────────── */
function addDamImages(map: MlMap): void {
  for (let p = 0; p <= 100; p += 10) addImg(map, "dam-" + p, (ctx) => drawTank(ctx, p, false));
  addImg(map, "dam-over", (ctx) => drawTank(ctx, 100, true));
  addImg(map, "dam-nodata", (ctx) => drawNoData(ctx));
  addImg(map, "dam-barrage", (ctx) => drawBarrage(ctx));
  addImg(map, "dam-arr-up", (ctx) => drawArrow(ctx, true), AW, AH);
  addImg(map, "dam-arr-down", (ctx) => drawArrow(ctx, false), AW, AH);
}
const IW = 40, IH = 48;
const AW = 26, AH = 26;
function addImg(map: MlMap, id: string, draw: (ctx: CanvasRenderingContext2D) => void, w = IW, h = IH): void {
  const c = document.createElement("canvas"); c.width = w; c.height = h;
  const ctx = c.getContext("2d"); if (!ctx) return;
  draw(ctx);
  putImage(map, id, ctx.getImageData(0, 0, w, h), { pixelRatio: 2 });
}
/** icon colour for the current mode (light: same hue, deeper) */
const ic = (c: string): string => (isLight() ? lightColor(c, "fill-color") : c);
/** small ▲ (amber, release up) / ▼ (green, release down) with a dark halo */
function drawArrow(ctx: CanvasRenderingContext2D, up: boolean): void {
  ctx.beginPath();
  if (up) { ctx.moveTo(13, 3); ctx.lineTo(23, 21); ctx.lineTo(3, 21); } else { ctx.moveTo(3, 5); ctx.lineTo(23, 5); ctx.lineTo(13, 23); }
  ctx.closePath();
  ctx.lineWidth = 3; ctx.strokeStyle = isLight() ? "#ffffff" : "#05080f"; ctx.lineJoin = "round"; ctx.stroke();
  ctx.fillStyle = ic(up ? "#ffb020" : "#22e39b"); ctx.fill();
}
function tankPath(ctx: CanvasRenderingContext2D): void {
  const x = 10, y = 6, w = 20, h = 36, r = 4;
  ctx.beginPath();
  ctx.moveTo(x + r, y); ctx.lineTo(x + w - r, y); ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r); ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h); ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r); ctx.arcTo(x, y, x + r, y, r); ctx.closePath();
}
function drawTank(ctx: CanvasRenderingContext2D, pct: number, over: boolean): void {
  const color = ic(over ? "#ff3b5c" : pctColor(pct));
  const x = 10, y = 6, w = 20, h = 36;
  // water fill from the bottom
  const fillH = Math.max(2, (h - 2) * Math.min(100, pct) / 100);
  ctx.save(); tankPath(ctx); ctx.clip();
  ctx.fillStyle = color; ctx.globalAlpha = 0.85;
  ctx.fillRect(x, y + h - fillH, w, fillH);
  ctx.globalAlpha = 1; ctx.restore();
  // outline + cap
  tankPath(ctx); ctx.lineWidth = 2.4; ctx.strokeStyle = color; ctx.stroke();
  ctx.fillStyle = color; ctx.fillRect(14, 3, 12, 3);
  if (over) { // "!" over-capacity mark
    ctx.font = "bold 16px sans-serif"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.fillStyle = "#ffffff"; ctx.fillText("!", 20, 24);
  }
}
function drawNoData(ctx: CanvasRenderingContext2D): void {
  tankPath(ctx); ctx.lineWidth = 2; ctx.strokeStyle = ic("#8aa0bf"); ctx.setLineDash([3, 3]); ctx.stroke(); ctx.setLineDash([]);
  ctx.fillStyle = ic("#8aa0bf"); ctx.font = "bold 18px sans-serif"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
  ctx.fillText("?", 20, 25);
}
function drawBarrage(ctx: CanvasRenderingContext2D): void {
  // discharge: downward arrow over wavy water, blue
  ctx.strokeStyle = ic("#2f7bff"); ctx.fillStyle = ic("#2f7bff"); ctx.lineWidth = 2.6; ctx.lineCap = "round";
  ctx.beginPath(); ctx.moveTo(20, 6); ctx.lineTo(20, 30); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(12, 23); ctx.lineTo(20, 32); ctx.lineTo(28, 23); ctx.closePath(); ctx.fill();
  ctx.lineWidth = 2; ctx.beginPath();
  for (let i = 0; i < 2; i++) { const yy = 38 + i * 5; ctx.moveTo(8, yy); ctx.quadraticCurveTo(14, yy - 3, 20, yy); ctx.quadraticCurveTo(26, yy + 3, 32, yy); }
  ctx.stroke();
}

/** Distinct river names along the downstream path (ปิง → เจ้าพระยา …). */
function flowNames(pathIds: number[]): string {
  const seen = new Set<string>(); const out: string[] = [];
  for (const id of pathIds) {
    const n = mainRiver(id)?.main ?? riverName(id); if (!n) continue; // main-river name, not a side-channel label
    const short = n.replace("แม่น้ำ", "").replace(/\s*\/.*$/, "").trim();
    if (short && !seen.has(short)) { seen.add(short); out.push(short); if (out.length >= 4) break; }
  }
  return out.length ? out.join(" → ") : "ลำน้ำ";
}

export function damCard(d: Dam, flowHtml = ""): string {
  const pct = showPct(d);
  const color = pct ? pctColor(d.percent) : pctColor(null);
  const over = pct && d.percent! > 100;
  const note = d.basisNote ? `<div class="muted small">ⓘ ${esc(d.basisNote)}</div>` : "";
  let bar: string;
  if (isBarrage(d)) {
    // เขื่อนทดน้ำ/ระบายน้ำ: show the official discharge if a source has one, else
    // state plainly WHY there is no % (reason 1) — never a bare "?".
    bar = d.barrageCms != null
      ? `<div class="dam-pct" style="color:#2f7bff">💧 ระบายท้ายเขื่อน ${fmt(d.barrageCms)} ลบ.ม./วิ</div>${deltaBlock(d)}
         <div class="muted small">เขื่อนทดน้ำ/ระบายน้ำ ไม่มีอ่างเก็บน้ำ จึงไม่มี % ความจุ</div>`
      : `<div class="muted small">🚧 เขื่อนทดน้ำ ไม่มีอ่างเก็บน้ำ จึงไม่มี % ความจุ${d.outflowCms != null ? ` · ระบาย ${fmt(d.outflowCms)} ลบ.ม./วิ` : ""}</div>`;
  } else if (pct) {
    // Official source % — always shown. Over-pool dams read red "เกินความจุ".
    // A carried-forward value (today's source silent) shows faded + its own date.
    // one % format everywhere (map label, card, 2554 table): a whole number — RID's 45.65 reads 46%
    const label = `${over ? "เกินความจุ · " : ""}${Math.round(d.percent!)}%${d.verified ? " (SWOC)" : ""}`;
    const staleNote = d.staleValue
      ? `<div class="muted small">ข้อมูล ณ ${esc(thaiDate(d.valueDate))} (วันนี้ยังไม่มีรายงาน)</div>` : "";
    bar = `<div class="dam-bar${d.staleValue ? " aged" : ""}"><div class="dam-bar-fill" style="width:${Math.min(100, d.percent!)}%;background:${color}"></div></div>
       <div class="dam-pct" style="color:${color}${d.staleValue ? ";opacity:.7" : ""}">${label}</div>${note}${staleNote}`;
  } else if (d.noDataReason === "inconsistent") {
    // reason 3: a value was reported but it is internally contradictory.
    const rep = d.swocPercent ?? d.percent;
    bar = `<div class="dam-pct" style="color:${pctColor(101)}">มีค่ารายงาน แต่ตัวเลขไม่สอดคล้องกัน</div>
       <div class="muted small">รายงาน ${rep ?? "?"}% · ปริมาตร/ความจุ = ${d.impliedPct ?? "?"}% — ดูประกาศ กรมชลประทาน</div>`;
  } else {
    // reason 2: the source simply did not report this dam in the latest round.
    bar = `<div class="muted small">แหล่งข้อมูล (กรมชลประทาน) ไม่ได้รายงานค่าของเขื่อนนี้ในรอบล่าสุด (ตรวจเมื่อ ${esc(thaiDate(d.updatedAt))})${d.egatUrl ? " · ดูข้อมูล กฟผ. ด้านล่าง" : ""}</div>`;
  }
  const trendTxt = d.trend === "up" ? "↑ น้ำไหลเข้ามากกว่าระบาย" : d.trend === "down" ? "↓ ระบายมากกว่าไหลเข้า" : d.trend === "flat" ? "→ ทรงตัว" : "";
  const link = d.egatUrl ? `<div style="margin-top:6px"><a href="${d.egatUrl}" target="_blank" rel="noopener">ดูข้อมูล/กล้อง กฟผ. ↗</a></div>` : "";
  // A missing flow is ALWAYS the words "ยังไม่มีรายงาน" — never a dash/0/blank.
  const flowRow = (label: string, mcmDay: number | null, cms: number | null): string =>
    `<span>${label}</span>` + (mcmDay == null ? `<b class="muted">ยังไม่มีรายงาน</b>`
      : mcmDay === 0 ? `<b>0 ล้าน ลบ.ม./วัน (0 ลบ.ม./วิ) · ตามรายงาน${d.verified ? " SWOC" : ""}</b>`
        : `<b>${fmt(mcmDay, 2)} ล้าน ลบ.ม./วัน (${fmt(cms)} ลบ.ม./วิ)</b>`);
  // v0.9.8a: the capacity shown is the base the % is computed on, named + sourced. The old "960" (ป่าสัก)
  // was not in any official source; RID's "ปริมาณน้ำสูงสุด" is not shown as a second capacity.
  const baseOkCard = d.capacityMcm != null && d.volumeMcm != null && d.percent != null && Math.abs((d.volumeMcm / d.capacityMcm) * 100 - d.percent) <= 2 && !d.baseMismatch;
  // Capacity and current volume are SEPARATE lines — never "วol/cap" or "–/225".
  const capLine = d.capacityMcm != null
    ? `${fmt(d.capacityMcm, d.capacityMcm % 1 ? 1 : 0)} ล้าน ลบ.ม. <span class="muted small">${baseOkCard ? "(ฐานที่กรมชลประทานใช้คิด % · ที่มา: กรมชลประทาน)" : "(ที่มา: กรมชลประทาน)"}</span>`
    : `<span class="muted">ยังไม่มีรายงาน</span>`;
  const volLine = d.volumeMcm != null
    ? `${fmt(d.volumeMcm)} ล้าน ลบ.ม.${d.percent != null ? ` (${Math.round(d.percent)}%)` : ""}`
    : `<span class="muted">ยังไม่มีรายงานวันนี้</span>`;
  const showGrid = !isBarrage(d) && d.noDataReason !== "diversion";
  const grid = showGrid
    ? `<div class="pop-grid">
        <span>${baseOkCard ? "ความจุ (ปริมาณน้ำเก็บกัก)" : "ความจุ"}</span><b>${capLine}</b>
        <span>ปริมาณน้ำตอนนี้</span><b>${volLine}</b>
        ${flowRow("ไหลเข้า", d.inflowMcmDay, d.inflowCms)}
        ${flowRow("ระบาย", d.outflowMcmDay, d.outflowCms)}
       </div>` : "";
  const aged = isAged(d);
  // A6: the card names its source in full once, at the top, with the data date
  const swoc = d.verified || (isBarrage(d) && d.barrageCms != null);
  const srcName = swoc ? SRC_FULL : d.source;
  const srcDate = isBarrage(d) && d.barrageCms != null ? (d.barrageDate ?? undefined) : d.valueDate;
  return `<div class="pop ${aged ? "stale" : ""}">
    <div class="pop-row"><b>${esc(d.name)}</b> <span class="muted small">${esc(d.operator)}</span></div>
    <div class="muted small pop-src">ที่มา: ${esc(srcName)} · ข้อมูล ณ ${esc(thaiDate(srcDate))}${aged ? " (อาจไม่เป็นปัจจุบัน)" : ""}</div>
    ${bar}${flowHtml}${grid}${isBarrage(d) ? "" : deltaBlock(d)}
    ${trendTxt ? `<div class="muted small" style="margin-top:4px">${trendTxt}</div>` : ""}
    ${isBarrage(d) ? (d.barrageCms != null && /เจ้าพระยา/.test(d.name) ? c13Max2011Html(d.barrageCms) : "") : compare2011Html(d as DamToday)}
    ${link}
  </div>`;
}
/** A3(ข): "ระบาย 400 ลบ.ม./วิ ▲ เพิ่มจากเมื่อวาน 170" + "ความจุ 111% ▲ จาก 110%".
 *  No previous report → a plain note, never an arrow (A5). */
function deltaBlock(d: Dam): string {
  if (d.staleValue) return "";
  const rc = d.relChange ?? null, pc = d.pctChange ?? null;
  const cur = isBarrage(d) ? d.barrageCms ?? null : d.outflowCms;
  if (!rc && !pc) {
    if (cur == null && d.percent == null) return "";
    return `<div class="dam-delta muted small">ยังไม่มีรายงานวันก่อนหน้าให้เทียบ</div>`;
  }
  const cls = (dir: string): string => (dir === "up" ? "up" : dir === "down" ? "down" : "same");
  const rows: string[] = [];
  if (rc) rows.push(`<div>ระบาย ${fmtCms(rc.cur)} ลบ.ม./วิ <b class="dd ${rc.arrow ? cls(rc.dir) : "same"}">${esc(releaseDeltaText(rc))}</b></div>`);
  if (d.streakText) rows.push(`<div class="muted small">${esc(d.streakText)}</div>`);
  if (pc) rows.push(`<div>ความจุ ${Math.round(pc.cur)}% <b class="dd ${cls(pc.dir)}">${esc(percentDeltaText(pc))}</b></div>`);
  const prevDate = rc?.prevDate ?? pc!.prevDate;
  return `<div class="dam-delta">${rows.join("")}<div class="muted small">เทียบรายงาน ${esc(thaiDay(prevDate))} (SWOC)</div></div>`;
}
function esc(s: string): string { return s.replace(/[<>&"]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;" }[c] ?? c)); }

/** v0.9.7 country-view toggle "เทียบปี 54": "ปี 54: X%" under each storage dam's %. */
export async function setDamsY54(map: MlMap, on: boolean): Promise<void> {
  await loadDam2011();
  const res = dams.filter((d) => !isBarrage(d) && d.noDataReason !== "diversion");
  setY54Labels(map, on, res as DamToday[], (name) => { const d = dams.find((x) => x.name === name); return d ? [d.lon, d.lat] : null; });
}
/** Today's record of a dam by name (summary row comparison). */
export function damToday(name: string): DamToday | null { return (dams.find((d) => d.name === name) as DamToday | undefined) ?? null; }
